import Tippy from '@tippyjs/react';
import { t } from 'i18next';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ReactComponent as Arrow } from '../../../assets/svg/ArrowDownPerm.svg';
import { ReactComponent as StatusBad } from '../../../img/icons/order-status-bad.svg';
import { ReactComponent as StatusNa, ReactComponent as Unknown } from '../../../img/icons/order-status-na.svg';
import { ReactComponent as Ok, ReactComponent as StatusOk } from '../../../img/icons/order-status-ok.svg';
import { ReactComponent as StatusWait } from '../../../img/icons/order-status-wait.svg';
import { ReactComponent as Crit } from '../../../img/icons/order-status-warning-red.svg';
import { ReactComponent as Warn } from '../../../img/icons/order-status-warning.svg';
import { AccessPermissionStatus, ContractStatus, CrmStatus, PortfolioRecord, useLazyGetPortfolioDetailQuery, useLazyGetPortfolioQuery } from '../../../services/content_v1';
import { useLazyGetAccountListQuery } from '../../../services/users_v1';
import { OuterMenuProps } from '../../../types/index';
import { toMplatDate } from '../../common';
import { CheckBox } from '../CheckBox';
import Table from '../DataTable/Table';
import TableBody from '../DataTable/TableBody';
import { TableColumn } from '../DataTable/TableColumn';
import TableHeader from '../DataTable/TableHeader';
import { TableHeaderColumn } from '../DataTable/TableHeaderColumn';
import TableRow from '../DataTable/TableRow';
import ImageLoader from '../ImageLoader';
import MultiSelect from '../MultiSelect';
import { Searcher } from '../Searcher';


export const getContractStatus = (s: ContractStatus) => {
    switch (s) {
        case ContractStatus.CONCLUDED:
            return <StatusOk />
        case ContractStatus.AGREEMENT:
            return <StatusWait />
        case ContractStatus.TERMINATED:
            return <StatusBad />;
        default:
            return <StatusNa />
    }
}

export const getContractStatusText = (s: ContractStatus) => {
    switch (s) {
        case ContractStatus.CONCLUDED:
            return <Trans>order:CONTRACT_CONCLUDED</Trans>
        case ContractStatus.AGREEMENT:
            return <Trans>order:CONTRACT_AGREEMENT</Trans>
        case ContractStatus.TERMINATED:
            return <Trans>order:CONTRACT_TERMINATED</Trans>
        default:
            return <Trans>Unknown</Trans>
    }
}

export function toPermissionStatus(c?: AccessPermissionStatus) {
    switch (c) {
        case AccessPermissionStatus.ISSUED:
            return <StatusOk />
        case AccessPermissionStatus.WORKING:
            return <StatusWait />
        case AccessPermissionStatus.REVOKED:
            return <StatusBad />
        default:
            return <StatusNa />
    }
}

export function toPermissionStatusText(c?: AccessPermissionStatus) {
    switch (c) {
        case AccessPermissionStatus.ISSUED:
            return <Trans>ISSUED</Trans>
        case AccessPermissionStatus.WORKING:
            return <Trans>WORKING</Trans>
        case AccessPermissionStatus.REVOKED:
            return <Trans>REVOKED</Trans>
        default:
            return <Trans>Unknown</Trans>
    }
}

export const getCrmStatus = (s: CrmStatus | null | undefined) => {
    switch (s) {
        case CrmStatus.OK:
            return <span className="portfolio-list__subs-status">
                <Ok />
            </span>
        case CrmStatus.WARN:
            return <Link to={''} className="portfolio-list__subs-status">
                <Warn />
            </Link>
        case CrmStatus.CRIT:
            return <Link to={''} className="portfolio-list__subs-status">
                <Crit />
            </Link>
        default:
            return <span className="portfolio-list__subs-status">
                <Unknown />
            </span>;
    }
};

interface MatrixTableRowProps {
    unit: PortfolioRecord
}

function MatrixTableRow({ unit }: MatrixTableRowProps) {
    const [fetchPortfolioDetail, { data }] = useLazyGetPortfolioDetailQuery();

    const [active, setActive] = useState(false);
    const [h, setH] = useState(0);
    const divRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (divRef.current) {
            setH(divRef.current.clientHeight);
        }
    }, [divRef.current, active, data?.results]);

    useEffect(() => {
        if (active && unit.unit && unit.provider && unit.consumer) {
            fetchPortfolioDetail({ offset: 0, limit: 0, consumer_id: unit.consumer.id, provider_id: unit.provider.id, unit_id: unit.unit.id });
        }
    }, [active]);

    return (<>
        <TableRow className={active ? '_active' : undefined} onClick={() => setActive((v) => !v)}>
            <TableColumn className="_id">{unit.unit ? `TV${unit.unit.id}` : 'Контент удален'}</TableColumn>
            <TableColumn className="_content">
                <div className="permissions-list__content-channels-item">
                    <ImageLoader url={unit.unit?.covers.at(0) || ''} alt="" className="permissions-list__content-channels-item-image" />
                    <div className="permissions-list__content-channels-item-body">
                        <div className="permissions-list__content-channels-item-name">{unit.unit?.title}</div>
                    </div>
                </div>
            </TableColumn>
            <TableColumn className="_subscriber">
                <Link to={`/account/view/${unit.consumer?.id}`}>{unit.consumer?.name}</Link>
            </TableColumn>
            <TableColumn className="_source">
                {unit.sources.map((s, i) => {
                    return <span key={s.id}>
                        {s.name}
                        {i === unit.sources.length - 1 ? '' : ', '}
                    </span>
                })}
            </TableColumn>
            <TableColumn className="_status">
                <span className="icon__status">
                    {getCrmStatus(unit.crm_status)}
                </span>
            </TableColumn>
            <TableColumn className="_collapsible-arrow">
                <span className="table-list__collapsible-arrow">
                    <Arrow />
                </span>
            </TableColumn>
        </TableRow>
        {active && (
            <div className="table-list__collapsible _active" style={{ display: 'block', height: `${h}px` }}>
                <div className="table-list__collapsible-body">
                    <div ref={divRef}>
                        {data?.results.data.map((detail, i) => <>
                            <div key={i} className="portfolio-list__item">
                                <div className="portfolio-list__item-col">
                                    <div className="portfolio-list__item-heading"><Trans>Contract</Trans></div>
                                    <div className="portfolio-list__item-list">
                                        <div className="portfolio-list__item-param">
                                            <div className="portfolio-list__item-param-name"><Trans>Number</Trans></div>
                                            <div className="portfolio-list__item-param-name"><Trans>Date</Trans></div>
                                            <div className="portfolio-list__item-param-name"><Trans>Status</Trans></div>
                                        </div>
                                        <div className="portfolio-list__item-param">
                                            <div className="portfolio-list__item-param-value">{detail?.contracts?.at(0)?.number || '-'}</div>
                                            <div className="portfolio-list__item-param-value">{detail.contracts?.at(0)?.concluded ? toMplatDate(detail.contracts?.at(0)?.concluded, true) : '-'}</div>
                                            <div className="portfolio-list__item-param-value">
                                                <Tippy
                                                    arrow={false}
                                                    content={getContractStatusText(detail.contracts?.at(0)?.status || 0)}
                                                    placement="right-start"
                                                    animation="shift-away"
                                                    theme="light"
                                                    duration={[100, 100]}
                                                >
                                                    <span className="icon__status">
                                                        {getContractStatus(detail.contracts?.at(0)?.status || 0)}
                                                    </span>
                                                </Tippy>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="portfolio-list__item-col">
                                    <div className="portfolio-list__item-heading"><Trans>Permission</Trans></div>
                                    <div className="portfolio-list__item-list">
                                        <div className="portfolio-list__item-param">
                                            <div className="portfolio-list__item-param-name"><Trans>Number</Trans></div>
                                            <div className="portfolio-list__item-param-name"><Trans>Date</Trans></div>
                                            <div className="portfolio-list__item-param-name"><Trans>Parameters</Trans></div>
                                            <div className="portfolio-list__item-param-name"><Trans>Status</Trans></div>
                                        </div>
                                        <div className="portfolio-list__item-param">
                                            <div className="portfolio-list__item-param-value">{detail.accesspermission?.id || '-'}</div>
                                            <div className="portfolio-list__item-param-value">{detail.accesspermission?.issued ? toMplatDate(detail.accesspermission?.issued, true) : '-'}</div>
                                            <div className="portfolio-list__item-param-value">{detail.accesspermission?.params.fmt.map((f) => f.name).join(', ')} - {detail.accesspermission?.params.version}</div>
                                            <div className="portfolio-list__item-param-value">
                                                <Tippy
                                                    arrow={false}
                                                    content={toPermissionStatusText(detail.accesspermission?.status)}
                                                    placement="right-start"
                                                    animation="shift-away"
                                                    theme="light"
                                                    duration={[100, 100]}
                                                >
                                                    <span className="icon__status">
                                                        {toPermissionStatus(detail.accesspermission?.status)}
                                                    </span>
                                                </Tippy>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="portfolio-list__item-col">
                                    <div className="portfolio-list__item-heading"><Trans>Access</Trans></div>
                                    <div className="portfolio-list__item-list">
                                        <div className="portfolio-list__item-param">
                                            <div className="portfolio-list__item-param-name"><Trans>TV signal</Trans></div>
                                            <div className="portfolio-list__item-param-name"><Trans>Source</Trans></div>
                                            <div className="portfolio-list__item-param-name"><Trans>Status</Trans></div>
                                        </div>
                                        {detail.signals.map((s) =>
                                            <div className="portfolio-list__item-param">
                                                <div className="portfolio-list__item-param-value">{s.name}</div>
                                                <div className="portfolio-list__item-param-value">{s.source.name}</div>
                                                <div className="portfolio-list__item-param-value">
                                                    <span className="icon__status">
                                                        {getCrmStatus(s.crm_status)}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </>)}
                    </div>
                </div>
            </div>
        )}
    </>);
}

function DealPortfolioProviderMatrix({ menu }: OuterMenuProps) {
    const [fetchAccounts, { data: accData }] = useLazyGetAccountListQuery();
    const [fetchPortfolio, { data: pData }] = useLazyGetPortfolioQuery();

    const [ordering, setOrdering] = useState('-id');
    const [search, setSearch] = useState<string>('');
    const [archive, setArchive] = useState(false);
    const [accFilter, setAccFilter] = useState<number[]>([]);
    const [typeFilter, setTypeFilter] = useState<number[]>([]);

    const accList = useMemo(() => {
        return accData?.results.data.map((u) => ({ value: u.name, label: u.name }));
    }, [accData]);

    const availTypes = useMemo(() => {
        return [
            { value: CrmStatus.OK, label: 'Ok' },
            { value: CrmStatus.WARN, label: t('Warning') },
            { value: CrmStatus.CRIT, label: t('Problem') },
        ];
    }, []);

    useEffect(() => {
        fetchAccounts({ offset: 0, limit: -1 });
    }, []);

    useEffect(() => {
        fetchPortfolio({ offset: 0, limit: -1, consumer_company: accFilter.join(','), crm_status: typeFilter.join(',') });
    }, [accFilter, typeFilter])

    return (
        <>
            <div className="main__content-filter">
                {menu}

                <div className="main__controls _no-sort">
                    <div className="main__controls-filter">
                        <Searcher value={search} onChange={(v) => setSearch(v)} placeholder={t('Search by registry')} />

                        <MultiSelect className="main__controls-filter-item _mid" placeholder={t('Consumer')}
                            value={accFilter} options={accList || []} setValue={(v) => setAccFilter(v)} minimized />
                        <MultiSelect className="main__controls-filter-item _mid _pre-checkbox" placeholder={t('Status')}
                            value={typeFilter} options={availTypes} setValue={(v) => setTypeFilter(v)} minimized />
                        <CheckBox value={archive} onChange={(v) => setArchive(v)} label={t('Archive')} />
                    </div>
                </div>
            </div>
            <Table className="_collapsible portfolio-list">
                <TableHeader>
                    <TableHeaderColumn header={'ID'} field="id" className="_datetime" ordering={ordering} setOrdering={setOrdering} />
                    <TableHeaderColumn header={t('Телеканал (контент)')} className="_content" />
                    <TableHeaderColumn header={t('Consumer')} className="_subscriber" />
                    <TableHeaderColumn header={t('Source')} className="_source" />
                    <TableHeaderColumn header={t('Status')} className="_status" />
                    <TableHeaderColumn header={''} className="_arrow" />
                </TableHeader>
                <TableBody>
                    {pData?.results.data.map((unit, i) => {
                        return <MatrixTableRow key={i} unit={unit} />
                    })}
                </TableBody>
            </Table>
        </>
    );
}

export default DealPortfolioProviderMatrix;
